import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock, CalendarDays, Calculator, Clock, Coins, Download, FileText, FileUp, Info, Link2, ListChecks, Loader2, Mail, Palmtree,
  Receipt, RefreshCw, type LucideIcon,
} from 'lucide-react';
import { api, type UploadResult } from '../lib/api';
import { vanzelfTekst } from '../components/AutomatischSection';
import { cn, formatDate } from '../lib/utils';
import {
  Card, CardLink, Disclosure, FileInput, ICON, IconButton, Kpi, ListRow, Notice, PageHeader, PageSkeleton, Sheet,
} from '../components/ui';

/**
 * Acties: alles wat ook automatisch gebeurt (hoe vaak: Instellingen, Automatisch)
 * als knop om het direct te starten.
 */

type UploadKind = 'loonstrook' | 'urenspec' | 'urencontrole' | 'rooster';

const UPLOAD_KINDS: Array<{ value: UploadKind; label: string }> = [
  { value: 'loonstrook', label: 'Loonstrook' },
  { value: 'urenspec', label: 'Urenbijlage (digitale bijlage loonstrook)' },
  { value: 'urencontrole', label: 'Urencontrole (Overzicht.pdf)' },
  { value: 'rooster', label: 'Rooster of roosterpreview (Overzicht.pdf)' },
];

function guessKind(name: string): UploadKind {
  const n = name.toLowerCase();
  if (/uren.?bijlage|urenspec|10056759_\d{6}/.test(n)) return 'urenspec';
  if (/urencontrole/.test(n)) return 'urencontrole';
  if (/rooster|overzicht/.test(n)) return 'rooster';
  return 'loonstrook';
}

/** Onderdelen in het kostenlogboek (worker lib/claude-gebruik.ts). */
const KOSTEN_LABEL: Record<string, string> = {
  loonstrook: 'Loonstroken lezen',
  urenspec: 'Urenbijlages lezen',
  rooster: 'Roosters en urencontroles lezen',
  document: 'Documenten herkennen',
  mail: 'Mails samenvatten',
  vragen: 'Vragen',
};
const dollar = (n: number) => `$ ${n.toFixed(2).replace('.', ',')}`;

const when = (iso: string | null | undefined) => (iso ? formatDate(iso, 'dd-MM HH:mm') : 'nog nooit');
const POCKET = 'Pocket-code nodig: vul hem in bij Instellingen, AFAS';

export function ActiesPage() {
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({ queryKey: ['acties-status'], queryFn: api.acties.status });
  const { data: afasStatus } = useQuery({ queryKey: ['afas-status'], queryFn: api.afas.status });
  const { data: auto } = useQuery({ queryKey: ['automatisch'], queryFn: api.automatisch.list });
  const { data: kosten } = useQuery({ queryKey: ['claude-kosten'], queryFn: api.acties.kosten });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['acties-status'] });
    qc.invalidateQueries({ queryKey: ['afas-status'] });
    qc.invalidateQueries({ queryKey: ['afwijkingen'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
    qc.invalidateQueries({ queryKey: ['claude-kosten'] });
    qc.invalidateQueries({ queryKey: ['shifts'] });
  };
  const [log, setLog] = useState<string[]>([]);
  const say = (line: string) =>
    setLog((l) => [`${new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}  ${line}`, ...l].slice(0, 30));
  const [uploadOpen, setUploadOpen] = useState(false);

  // AFAS: eerst nieuwe loonstroken en urenbijlages, daarna de overige tabbladen (twee per aanroep, gratis plan).
  const afas = useRunner(
    async (update) => {
      update({ step: 'Loonstroken en urenbijlages ophalen' });
      const r = await api.afas.sync();
      if (r.status === 'needs_2fa') return { ok: false, message: POCKET, details: [] };
      if (r.status !== 'ok') return { ok: false, message: `Mislukt: ${leesbareFout(String(r.error ?? r.status))}`, details: [] };
      const docs = r.imported.map((i) => `${i.kind === 'loonstrook' ? 'Loonstrook' : 'Urenbijlage'} periode ${i.period} ${i.year} ingelezen`);

      const opgehaald: string[] = [...(r.portal?.fetched ?? [])];
      const wijzigingen: string[] = [...(r.portal?.changes ?? [])];
      update({ step: 'Gegevens per tabblad ophalen' });
      for (let i = 0; i < 6; i++) {
        const p = await api.afas.portalSync();
        if (p.status && p.status !== 'ok') {
          return {
            ok: false,
            message: p.status === 'needs_2fa' ? POCKET : `Documenten klaar, gegevens mislukt: ${p.error ?? p.status}`,
            details: docs,
          };
        }
        opgehaald.push(...p.fetched);
        wijzigingen.push(...p.changes);
        update({ done: opgehaald.length, total: opgehaald.length + p.remaining, step: `${opgehaald.length} tabblad(en) opgehaald` });
        if (!p.remaining || !p.fetched.length) break;
      }
      const delen = [
        docs.length ? `${docs.length} document(en) ingelezen` : 'geen nieuwe documenten',
        opgehaald.length ? `${opgehaald.length} tabblad(en) bijgewerkt` : 'gegevens al bijgewerkt',
        wijzigingen.length ? `${wijzigingen.length} wijziging(en)` : null,
      ].filter(Boolean) as string[];
      const message = delen.join(', ');
      return {
        ok: true,
        message: message.charAt(0).toUpperCase() + message.slice(1),
        details: [...docs, ...wijzigingen.map((w) => `Loon: ${w}`), ...(opgehaald.length ? [`Opgehaald: ${opgehaald.join(', ')}`] : [])],
      };
    },
    (res) => {
      say(`AFAS: ${res.message}`);
      refresh();
      qc.invalidateQueries({ queryKey: ['uitkeringen'] });
      qc.invalidateQueries({ queryKey: ['belangrijke-dagen'] });
    }
  );

  const gmail = useRunner(async (update) => {
    // Gmail verwerkt per keer een handvol mails; doorgaan tot er niets meer klaarstaat.
    const details: string[] = [];
    for (let round = 0; round < 10; round++) {
      const r = await api.gmail.sync();
      if (r.status !== 'ok') {
        const message = r.status === 'scope_missing' ? 'Koppel Google opnieuw bij Instellingen' : `Mislukt: ${r.error ?? r.status}`;
        return { ok: false, message, details };
      }
      details.push(...r.processed.map((m) => m.summary ?? m.subject));
      update({ done: details.length, total: details.length + r.remaining, step: `${details.length} mail(s) gelezen` });
      if (!r.remaining || !r.processed.length) break;
    }
    return { ok: true, message: details.length ? `${details.length} mail(s) gelezen` : 'Geen nieuwe mails', details };
  }, (res) => { say(`Gmail: ${res.message}`); refresh(); });

  const archief = useRunner(async (update) => {
    // Elke ronde een hap mails (oudste geschiedenis inbegrepen); doorgaan tot de hele mailbox gehad is.
    const details: string[] = [];
    let dubbel = 0;
    let fouten = 0;
    for (let round = 0; round < 60; round++) {
      const r = await api.gmail.archief();
      if (r.status !== 'ok') return { ok: false, message: `Mislukt: ${r.error ?? r.status}`, details };
      details.push(...r.nieuw.map((d) => d.titel));
      dubbel += r.dubbel;
      fouten += r.fouten;
      update({ done: r.bekeken, total: Math.max(r.totaal ?? 0, r.bekeken), step: `${r.bekeken} mails bekeken, ${details.length} nieuwe document(en)` });
      if (r.klaar) break;
    }
    const delen = [`${details.length} nieuwe document(en)`, dubbel ? `${dubbel} stonden er al` : null, fouten ? `${fouten} mislukt` : null].filter(Boolean);
    return { ok: true, message: delen.join(', '), details };
  }, (res) => { say(`Mailbijlagen: ${res.message}`); refresh(); qc.invalidateQueries({ queryKey: ['documents'] }); });

  const rooster = useRunner(async () => {
    const r: any = await api.rooster.icsSync();
    const changed = (r.created ?? 0) + (r.updated ?? 0) + (r.removed ?? 0);
    const message = changed ? `${r.created ?? 0} nieuw, ${r.updated ?? 0} gewijzigd, ${r.removed ?? 0} verwijderd` : 'Rooster ongewijzigd';
    return { ok: true, message, details: [] };
  }, (res) => { say(`Rooster: ${res.message}`); refresh(); });

  const agenda = useRunner(async () => {
    const r: any = await api.calendar.reconcile();
    return {
      ok: true,
      message: `${r.enqueued ?? 0} afspraken bij te werken`,
      details: [`${r.duplicates_removed ?? 0} dubbele afspraken opgeruimd`, `${r.orphans_removed ?? 0} oude afspraken opgeruimd`],
    };
  }, (res) => { say(`Agenda: ${res.message}`); refresh(); });

  const verlof = useRunner(async () => {
    const r = await api.acties.verlofAanvullen();
    const n = r.created + r.converted;
    return { ok: true, message: n ? `${n} verlofdag(en) toegevoegd, ${r.hours} uur` : 'Alle verlofdagen stonden er al', details: [] };
  }, (res) => { say(`Verlof: ${res.message}`); refresh(); });

  const recalc = useRunner(async (update) => {
    let after = 0;
    let done = 0;
    let errors = 0;
    let retries = 0;
    for (let i = 0; i < 300; i++) {
      try {
        const r = await api.acties.recalcBatch(after);
        if (!r.report.length) break;
        done += r.report.length;
        errors += r.shifts_with_error ?? 0;
        after = r.report[r.report.length - 1].shift_id;
        update({ done, total: done + (r.remaining ?? 0), step: `${done} diensten doorgerekend` });
        if (r.remaining === 0) break;
      } catch {
        // Gratis plan: een batch kan op de CPU-limiet stuiten; even wachten en opnieuw.
        if (++retries > 20) return { ok: false, message: `Gestopt na ${done} diensten: de server bleef weigeren`, details: [] };
        update({ step: `Server even bezet, ${done} diensten klaar, opnieuw proberen (${retries})` });
        await new Promise((res) => setTimeout(res, 1500));
      }
    }
    return {
      ok: errors === 0,
      message: errors ? `${done} diensten doorgerekend, ${errors} met een fout` : `${done} diensten doorgerekend`,
      details: [],
    };
  }, (res) => { say(`Herberekenen: ${res.message}`); refresh(); });

  if (isLoading) return <PageSkeleton kpis={4} />;

  const afasOk = afasStatus?.status === 'ok';
  const afasText =
    afasStatus?.status === 'needs_2fa'
      ? 'Pocket-code nodig'
      : afasOk
        ? afasStatus?.trusted_until
          ? `Gekoppeld tot ${formatDate(afasStatus.trusted_until, 'dd-MM-yyyy')}`
          : 'Gekoppeld'
        : afasStatus?.status === 'not_configured'
          ? 'Niet ingesteld'
          : afasStatus?.status === 'error'
            ? 'Fout'
            : afasStatus
              ? 'Nog niet opgehaald'
              : '–';

  return (
    <div className="page">
      <PageHeader
        title="Acties"
        sub="Alles hieronder gebeurt ook vanzelf; met een knop doe je het meteen. Er gaat nooit iets naar Trigion of de planner."
        actions={
          <button type="button" className="btn-secondary" onClick={() => setUploadOpen(true)}>
            <FileUp className={ICON.sm} aria-hidden />
            PDF uploaden
          </button>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Kpi icon={CalendarDays} label="Diensten" value={status?.counts?.shifts} to="/kalender" />
        <Kpi icon={FileText} label="Documenten" value={status?.counts?.documents} to="/documenten" />
        <Kpi icon={Receipt} label="Loonstroken" value={status?.counts?.loonstroken} />
        <Kpi icon={Clock} label="Urenbijlages" value={status?.counts?.urenspecs} />
      </div>

      <Card flush title="Ophalen en bijwerken" icon={RefreshCw}>
        <div className="list border-t border-surface-line">
          <ActionRow
            icon={Download}
            title="AFAS ophalen"
            info={`Nieuwe loonstroken en urenbijlages inlezen, daarna salaris, contract, rooster, functie, verzuim, kwalificaties, jaaropgaven en de proefberekening. Je loon in de app wordt gelijkgezet aan AFAS. ${vanzelfTekst(auto, 'afas')}`}
            meta={`Laatst ${when(status?.afas?.last_sync_at)}${status?.afas?.status === 'needs_2fa' ? ', Pocket-code nodig' : ''}`}
            metaWarn={status?.afas?.status === 'needs_2fa'}
            action="Ophalen"
            run={afas}
            expected="meestal 30 seconden tot 2 minuten"
            steps={['Inloggen bij AFAS InSite', 'Nieuwe loonstroken en urenbijlages lezen en inlezen', 'Gegevens per tabblad ophalen en je loon vergelijken']}
          />
          <ActionRow
            icon={Mail}
            title="Trigion-mail lezen"
            info={`Roosters en urencontroles uit je mail met de app vergelijken. Een rooster-mail wordt meteen in je diensten gezet. Wat er verandert, krijg alleen jij te zien. ${vanzelfTekst(auto, 'gmail')}`}
            meta={`${status?.gmail?.n ?? 0} mails gelezen, laatst ${when(status?.gmail?.last)}`}
            action="Lezen"
            run={gmail}
            expected="meestal 10 seconden tot 1 minuut"
            steps={['Nieuwe mails van Trigion zoeken', 'Elke mail laten lezen en samenvatten', 'Urencontroles en roosters vergelijken met je diensten']}
          />
          <ActionRow
            icon={FileText}
            title="Mailbijlagen archiveren"
            info={`Alle PDF's uit je Trigion-mail, de hele geschiedenis, één voor één laten lezen en bij Documenten zetten. Bestanden die er al staan worden herkend zonder ze opnieuw te lezen. ${vanzelfTekst(auto, 'archief')}`}
            meta={
              status?.archief?.klaar
                ? `Hele mailbox gehad, ${status.archief.nieuw} document(en) uit mail`
                : `${status?.archief?.bekeken ?? 0}${status?.archief?.totaal ? ` van ~${status.archief.totaal}` : ''} mails bekeken, ${status?.archief?.nieuw ?? 0} document(en) uit mail`
            }
            action="Archiveren"
            run={archief}
            expected="per ronde 20 seconden tot een minuut; loopt door tot alles gehad is"
            steps={['Trigion-mail met bijlagen doorlopen', 'Dubbele bestanden herkennen', "Nieuwe PDF's laten lezen en sorteren"]}
          />
          <ActionRow
            icon={RefreshCw}
            title="Rooster ophalen"
            info={`Diensten uit RostarCAS in de kalender zetten. ${vanzelfTekst(auto, 'rooster')}`}
            meta={`Laatst ${when(status?.rooster?.ics_last_fetched_at)}`}
            action="Ophalen"
            run={rooster}
            expected="meestal een paar seconden"
            steps={['Roosterfeed van RostarCAS ophalen', 'Nieuwe, gewijzigde en vervallen diensten bepalen', 'Loon van gewijzigde diensten doorrekenen']}
          />
          <ActionRow
            icon={CalendarClock}
            title="Google-agenda bijwerken"
            info={`Diensten naar de agenda 'Werk - Diensten' zetten en dubbele afspraken opruimen. ${vanzelfTekst(auto, 'agenda')}`}
            meta={`Laatst ${when(status?.agenda.last_sync_at)}, ${status?.agenda.wachtrij ?? 0} in de wachtrij`}
            action="Bijwerken"
            run={agenda}
            expected="meestal een paar seconden"
            steps={['Diensten vergelijken met de agenda', 'Afspraken klaarzetten om toe te voegen of bij te werken', 'Dubbele en oude afspraken opruimen']}
          />
          <ActionRow
            icon={Palmtree}
            title="Verlof aanvullen"
            info="Verlofdagen van je urenbijlages die niet in je rooster staan als verlofdienst toevoegen."
            meta="Alleen met de hand"
            action="Aanvullen"
            run={verlof}
            expected="meestal een paar seconden"
            steps={['Urenbijlages doorlopen op verlofregels', 'Ontbrekende verlofdagen als dienst toevoegen']}
          />
          <ActionRow
            icon={Calculator}
            title="Alles herberekenen"
            info="Het loon van alle diensten opnieuw doorrekenen, bijvoorbeeld na een CAO- of loonwijziging."
            meta="Alleen met de hand"
            action="Herberekenen"
            run={recalc}
            expected="ongeveer 1 tot 2 minuten"
            steps={['Diensten in kleine groepen doorrekenen']}
          />
        </div>
      </Card>

      <div className="grid gap-3 sm:gap-4 lg:grid-cols-2 [&>*]:min-w-0">
        <Card flush title="Koppelingen" icon={Link2} action={<CardLink to="/instellingen#afas">Beheren</CardLink>}>
          <div className="list border-t border-surface-line">
            <StatusRow label="AFAS" ok={afasOk} text={afasText} />
            <StatusRow label="PDF's en mails lezen" ok={!!afasStatus?.configured} text={afasStatus?.configured ? 'Ingesteld' : 'Niet ingesteld'} />
            <StatusRow
              label="Google-agenda en Gmail"
              ok={status?.agenda.last_sync_status === 'ok'}
              text={`Laatst ${when(status?.agenda.last_sync_at)}`}
            />
          </div>
        </Card>

        <Card flush title="Nog niet binnen" icon={ListChecks} action={<CardLink to="/afwijkingen">Afwijkingen</CardLink>}>
          <div className="list border-t border-surface-line">
            {status?.ontbrekend.length ? (
              status.ontbrekend.map((m, i) => (
                <ListRow
                  key={`${m.year}-${m.period}`}
                  i={i}
                  title={`Periode ${m.period} · ${m.year}`}
                  value={<span className="status-warn">{[!m.loonstrook && 'Loonstrook', !m.urenbijlage && 'urenbijlage'].filter(Boolean).join(' en ').replace(/^u/, 'U')}</span>}
                />
              ))
            ) : (
              <div className="row">
                <span className="status-ok">Alle loonstroken en urenbijlages zijn binnen</span>
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card flush title="Claude-kosten deze maand" icon={Coins} action={kosten ? <span className="t-num text-sm text-slate-100">{dollar(kosten.totaal)}</span> : null}>
        <div className="list border-t border-surface-line">
          {kosten?.per_onderdeel.length ? (
            kosten.per_onderdeel.map((k, i) => (
              <ListRow
                key={k.onderdeel}
                i={i}
                title={KOSTEN_LABEL[k.onderdeel] ?? k.onderdeel}
                meta={`${k.aanroepen} ${k.aanroepen === 1 ? 'aanroep' : 'aanroepen'}`}
                value={dollar(k.kosten)}
              />
            ))
          ) : (
            <div className="row">
              <span className="text-sm text-slate-400">Deze maand nog geen aanroepen.</span>
            </div>
          )}
          <div className="row">
            <span className="text-xs text-slate-400">
              Schatting per aanroep{kosten?.gemeten_sinds ? `, gemeten sinds ${formatDate(kosten.gemeten_sinds)}` : ''}. Het echte bedrag staat in de Anthropic Console.
            </span>
          </div>
        </div>
      </Card>

      {!!log.length && (
        <div className="card-flush enter">
          <Disclosure title="Logboek" right={<span className="badge-count">{log.length}</span>}>
            <ul className="space-y-1 text-sm text-slate-300">
              {log.map((l, i) => (
                <li key={i} className="t-num break-words">{l}</li>
              ))}
            </ul>
          </Disclosure>
        </div>
      )}

      <UploadSheet
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onDone={(r) => {
          say(`Upload: ${r.message}`);
          refresh();
        }}
        onError={(m) => say(`Upload: ${m}`)}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function StatusRow({ label, ok, text }: { label: string; ok: boolean; text: string }) {
  return (
    <div className="row">
      <span className="min-w-0 flex-1 truncate text-sm text-slate-100">{label}</span>
      <span className={ok ? 'status-ok' : 'status-warn'}>{text}</span>
    </div>
  );
}

function ActionRow(props: {
  icon: LucideIcon;
  title: string;
  info: string;
  meta: string;
  metaWarn?: boolean;
  action: string;
  run: Runner;
  expected: string;
  steps: string[];
}) {
  const Icon = props.icon;
  const { state, start } = props.run;
  const busy = state.phase === 'running';
  const [showInfo, setShowInfo] = useState(false);

  let line: ReactNode = <span className={cn('truncate', props.metaWarn && 'text-amber-200')}>{props.meta}</span>;
  if (state.phase === 'running') line = <RunningLine state={state} />;
  if (state.phase === 'done')
    line = (
      <span className={cn('truncate', state.ok ? 'text-money' : 'text-red-300')} title={state.message}>
        {state.message}
      </span>
    );

  return (
    <div>
      <div className="row pr-3 sm:pr-4">
        <Icon className={cn(ICON.sm, 'shrink-0 text-slate-400')} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-slate-100">{props.title}</div>
          <div className="flex min-w-0 text-xs text-slate-400">{line}</div>
        </div>
        <IconButton
          icon={Info}
          label={showInfo ? 'Uitleg verbergen' : 'Wat doet dit?'}
          onClick={() => setShowInfo((v) => !v)}
          className={cn('shrink-0', showInfo && 'text-slate-100')}
        />
        <button type="button" className="btn-secondary btn-sm shrink-0" disabled={busy} onClick={start}>
          {busy && <Loader2 className={cn(ICON.sm, 'animate-spin')} aria-hidden />}
          {busy ? 'Bezig' : props.action}
        </button>
      </div>
      {(showInfo || state.phase !== 'idle') && (
        <div className="space-y-3 px-4 pb-3 sm:pl-12 sm:pr-5">
          {showInfo && <p className="animate-enter t-muted">{props.info}</p>}
          {state.phase === 'running' && <RunningPanel state={state} expected={props.expected} steps={props.steps} />}
          {state.phase === 'done' && <ResultPanel state={state} />}
        </div>
      )}
    </div>
  );
}

/** Eén regel in de rij zelf: stap en verstreken tijd. */
function RunningLine({ state }: { state: Extract<RunState, { phase: 'running' }> }) {
  const elapsed = useElapsed(state.startedAt);
  return (
    <span className="truncate text-brand-300">
      {state.step ?? 'Bezig'} · <span className="t-num">{fmtElapsed(elapsed)}</span>
    </span>
  );
}

function RunningPanel({ state, expected, steps }: { state: Extract<RunState, { phase: 'running' }>; expected: string; steps: string[] }) {
  const elapsed = useElapsed(state.startedAt);
  const pct = state.total ? Math.min(100, Math.round(((state.done ?? 0) / state.total) * 100)) : null;
  return (
    <div className="animate-enter rounded-lg border border-surface-line bg-surface-raised p-3" role="status" aria-live="polite">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 text-slate-200">
          {state.step ?? 'Bezig'}
          {pct != null && <span className="t-num text-slate-400">, {pct}%</span>}
        </span>
        <span className="t-num shrink-0 text-xs text-slate-400">{fmtElapsed(elapsed)}</span>
      </div>
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-surface-line">
        {pct != null ? (
          <div className="h-full bg-brand-400 transition-[width] duration-slow ease-calm" style={{ width: `${pct}%` }} />
        ) : (
          <div className="h-full w-2/5 animate-progress-slide bg-brand-400/80" />
        )}
      </div>
      <ol className="mt-3 space-y-0.5 text-xs text-slate-400">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-2">
            <span className="t-num text-slate-500">{i + 1}.</span>
            {s}
          </li>
        ))}
      </ol>
      <p className="mt-2 text-xs text-slate-500">Duurt {expected}. Laat deze pagina open tot het klaar is.</p>
    </div>
  );
}

function ResultPanel({ state }: { state: Extract<RunState, { phase: 'done' }> }) {
  return (
    <div className="animate-enter text-xs text-slate-400" role="status">
      <span className="t-num">
        Klaar om {new Date(state.at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}, duurde {fmtElapsed(state.ms)}
      </span>
      {!!state.details.length && (
        <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-slate-300">
          {state.details.slice(0, 12).map((d, i) => (
            <li key={i} className="break-words">{d}</li>
          ))}
          {state.details.length > 12 && <li className="list-none text-slate-500">en nog {state.details.length - 12}</li>}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function UploadSheet({
  open, onClose, onDone, onError,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (r: UploadResult) => void;
  onError: (m: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<UploadKind>('loonstrook');
  const [result, setResult] = useState<UploadResult | null>(null);
  const mut = useMutation({
    mutationFn: () => api.acties.upload(kind, file!),
    onSuccess: (r) => {
      setResult(r);
      onDone(r);
    },
    onError: (e) => {
      setResult({ ok: false, kind, message: (e as Error).message });
      onError((e as Error).message);
    },
  });

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="PDF uploaden"
      sub="Een loonstrook of urenbijlage wordt ingelezen; een urencontrole of rooster wordt vergeleken met je diensten."
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          {mut.isPending && (
            <span className="mr-auto text-xs text-slate-400">
              <UploadElapsed /> Meestal 10 tot 40 seconden.
            </span>
          )}
          <button type="button" className="btn-primary" disabled={!file || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending && <Loader2 className={cn(ICON.sm, 'animate-spin')} aria-hidden />}
            {mut.isPending ? 'Bezig met lezen' : 'Uploaden'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <span className="label">Bestand</span>
          <FileInput
            accept="application/pdf"
            file={file}
            onChange={(f) => {
              setFile(f);
              setResult(null);
              if (f) setKind(guessKind(f.name));
            }}
          />
        </div>
        <div>
          <label className="label" htmlFor="upload-kind">Soort document</label>
          <select id="upload-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as UploadKind)}>
            {UPLOAD_KINDS.map((k) => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </div>
        {result && <Notice tone={result.ok ? 'ok' : 'err'}>{result.message}</Notice>}
        {!!result?.diffs?.length && (
          <table className="table">
            <thead>
              <tr>
                <th className="!px-0">Datum</th>
                <th>Verschil</th>
                <th className="!pr-0 text-right">Trigion / app</th>
              </tr>
            </thead>
            <tbody>
              {result.diffs.map((d, i) => (
                <tr key={i}>
                  <td className="t-num !px-0 text-slate-100">{/^\d{4}-\d{2}-\d{2}$/.test(d.date) ? formatDate(d.date) : d.date}</td>
                  <td className="text-slate-300">{d.type.replace(/_/g, ' ')}</td>
                  <td className="t-num !pr-0 text-right text-slate-300">
                    {d.trigion ?? '–'} / {d.app ?? '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Sheet>
  );
}

// Voortgang per actie

type RunResult = { ok: boolean; message: string; details: string[] };
type Progress = { done?: number; total?: number; step?: string };
type RunState =
  | { phase: 'idle' }
  | ({ phase: 'running'; startedAt: number } & Progress)
  | ({ phase: 'done'; at: number; ms: number } & RunResult);
type Runner = { state: RunState; start: () => void };

/** Maakt een foutmelding leesbaar: geen ruwe HTML of JSON van een andere server. */
function leesbareFout(msg: string): string {
  if (/http_5\d\d|Runtime Error|HTTP 5\d\d/i.test(msg)) {
    return 'De andere kant (RostarCAS of AFAS) heeft een storing. Probeer het later opnieuw; de app probeert het zelf later opnieuw.';
  }
  if (/http_(401|403|404|410)/i.test(msg)) return 'De link of inlog werkt niet meer. Werk hem bij in Instellingen.';
  const kaal = msg.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return kaal.length > 160 ? `${kaal.slice(0, 160)}…` : kaal;
}

/** Draait een actie en houdt bij waar hij is: bezig (met echte voortgang als die er is), klaar of mislukt. */
function useRunner(task: (update: (p: Progress) => void) => Promise<RunResult>, onDone?: (r: RunResult) => void): Runner {
  const [state, setState] = useState<RunState>({ phase: 'idle' });
  async function run() {
    const startedAt = Date.now();
    setState({ phase: 'running', startedAt });
    const update = (p: Progress) => setState((s) => (s.phase === 'running' ? { ...s, ...p } : s));
    let res: RunResult;
    try {
      res = await task(update);
    } catch (e) {
      res = { ok: false, message: `Mislukt: ${leesbareFout((e as Error).message)}`, details: [] };
    }
    setState({ phase: 'done', at: Date.now(), ms: Date.now() - startedAt, ...res });
    onDone?.(res);
  }
  return { state, start: () => void run() };
}

function UploadElapsed() {
  const [since] = useState(Date.now());
  return <span className="t-num text-slate-300">{fmtElapsed(useElapsed(since))}.</span>;
}

function useElapsed(since: number) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now - since;
}

function fmtElapsed(ms: number) {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} min`;
}
